import type { SagaReturnType } from 'redux-saga/effects';
import { all, delay, put, select, takeLatest } from 'redux-saga/effects';

import { api } from 'src/api';
import { logger } from 'src/error';

// Selectors:
import { getClientData, getClientStatsData } from 'src/store/client/selectors';
import {
  selectOrderID,
  getPaymentAmount,
  getPaymentMethod,
} from 'src/store/payment/selectors';
import { getIsMobile } from 'src/store/view/selectors';

// Actions:
import { setOrderInfo, setPaymentMethod } from 'src/store/payment/slice';
import { initPayment } from 'src/store/payment/actions';
import {
  createCertificateAction,
  createCertificateFailedAction,
  createCertificateSuccessAction,
  downloadCertificateAction,
  downloadCertificateFailedAction,
  downloadCertificateSuccessAction,
  submitCertificateAction,
} from '../store/certificate/sagaActions';

// Cert selectors:
import {
  certificateStateSelf,
  getIsCertificateCreated,
} from '../store/certificate/selectors';

import {
  CERTIFICATE_DOWNLOAD_MAX_RETRY,
  NO_ATTACHMENT_ERROR,
} from '../store/certificate/constants';

function* handleSubmitCertificate() {
  const isCreated = yield select(getIsCertificateCreated);

  if (isCreated) {
    yield put(initPayment());
  } else {
    yield put(createCertificateAction());
  }
}

/**
 * Отправляет события ошибки создания или оплаты заказа
 * на бэкенд
 */
export function* sendCertificateFailAnalytics(action) {
  try {
    const { payload } = action;

    const client: SagaReturnType<typeof getClientStatsData> = yield select(
      getClientStatsData,
    );
    const { id } = client;
    if (!id) throw new Error('No client id provided');
    const error =
      'analyticsError' in payload ? payload.analyticsError : payload.message;

    const response: SagaReturnType<typeof api.postStatsCertificate> =
      yield api.postStatsCertificate({
        event: 'certificate_creation_failed',
        error,
        client: { ...client, id },
        url: window.location.href,
      });
    const { status, success } = response;

    if (!success) {
      logger(status, 'sendCertificateFailAnalytics');
    }
  } catch (error) {
    logger(error, 'sendCertificateFailAnalytics');
  }
}

function* handleCreateCertificate() {
  const { value, holder, design, text, shippingType } = yield select(
    certificateStateSelf,
  );
  const client = yield select(getClientData);
  const source = (yield select(getIsMobile)) ? 'mobile' : 'desktop';
  yield put(setPaymentMethod('card'));
  const paymentOption = yield select(getPaymentMethod);
  const paymentAmount = yield select(getPaymentAmount);

  const params = {
    client,
    payment_option: paymentOption,
    source,
    gift: {
      template: design,
      amount: paymentAmount || value,
      receiver_name: holder || '',
      greeting_text: text || '',
      delivery_type: shippingType,
    },
  };

  try {
    const {
      order: { id, secret_stamp },
      // @ts-ignore
    } = yield api.postCertificatesCreate(params);

    yield put(setOrderInfo({ orderId: id, pin: secret_stamp }));
    yield put(createCertificateSuccessAction());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    logger(error, 'handleCreateCertificate');

    const errorResponse = 'response' in error ? error.response : {};
    const { status, data } = errorResponse;

    yield put(
      createCertificateFailedAction({
        // Если postCertificatesCreate вернул ошибку, передаём её в payload'е
        analyticsError: `${status}: ${data?.error || 'Unknown server error'}`,
      }),
    );
  }
}

function* handleCertificateCreated() {
  yield put(initPayment());
}

function* handleCertDownload(action) {
  try {
    const orderId = yield select(selectOrderID);
    const { error_code, link_to_pdf } = yield api.getCertificatesPdf({
      order_id: orderId,
    });

    if (link_to_pdf) {
      yield put(downloadCertificateSuccessAction(link_to_pdf));
    } else {
      const retryCount = action.payload || 1;
      const retry =
        error_code === NO_ATTACHMENT_ERROR &&
        retryCount <= CERTIFICATE_DOWNLOAD_MAX_RETRY;

      if (retry) {
        const delayMs = retryCount * 5000;
        yield delay(delayMs);
        yield put(downloadCertificateAction(retryCount + 1));
      } else {
        throw new Error(error_code);
      }
    }
  } catch (error) {
    const errMsg = 'Gift: Ошибка при получении pdf сертификата.';
    logger(error, errMsg);
    yield put(downloadCertificateFailedAction({ message: errMsg }));
  }
}

export default function* certReducer() {
  yield all([
    takeLatest(submitCertificateAction, handleSubmitCertificate),
    takeLatest(createCertificateAction, handleCreateCertificate),
    takeLatest(createCertificateSuccessAction, handleCertificateCreated),
    takeLatest(downloadCertificateAction, handleCertDownload),
  ]);
}
